import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import {
  ExternalDataService,
  ScrapCollection,
  Zone,
  Agent,
  VoucherSettlementStatus,
  BulkUpdateVoucherSettlementDto,
  FeedbackQuestionDefinitionDto,
  FeedbackQuestionAnswerDto,
  SaveScrapCollectionFeedbackDto,
  TagScrapCollectionFeedbackDto,
} from '../../services/externalDataService.service';
import { AuthService } from '../../services/auth.service';
import { PaginationComponent } from '../shared/pagination/pagination.component';

@Component({
  selector: 'app-scrap-collections-list',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent],
  templateUrl: './scrap-collections-list.component.html',
  styleUrl: './scrap-collections-list.component.css',
})
export class ScrapCollectionsListComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private readonly STORAGE_KEY = 'scrap_collections_list_state';

  // Data properties
  scrapCollections: ScrapCollection[] = [];
  zones: Zone[] = [];
  agents: Agent[] = [];

  // Loading and error states
  loading = false;
  errorMessage = '';

  // Pagination properties
  currentPage = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages: number = 1;

  // Filter properties
  filters = {
    dateFrom: '',
    dateTo: '',
    geoZoneId: '',
    customerName: '',
    phoneNumber: '',
    agentId: '',
    paymentMethod: 'all',
    voucherSettlementStatus: 'all',
    feedbackStatus: 'all',
  };

  // Selection properties
  activeTab: 'all' | 'voucher' | 'feedback' = 'all';
  selectedCollectionIds: Set<string> = new Set();
  selectedAgentId: string | null = null;
  showSettlementModal = false;
  settling = false;
  settlementStatus = VoucherSettlementStatus.Settled;
  settlementNotes = '';
  dateOfCollection = '';
  VoucherSettlementStatus = VoucherSettlementStatus;
  showSettlementDetailsModal = false;
  selectedSettlementCollection: ScrapCollection | null = null;
  settlementErrorMessage = '';

  // Feedback Modal & Form Properties
  showFeedbackModal = false;
  selectedFeedbackCollection: ScrapCollection | null = null;
  feedbackHappinessIndex: number | null = null;
  feedbackCustomerComments = '';
  feedbackInternalComments = '';
  feedbackStatus = 'Completed';
  surveyQuestionAnswers: { [questionId: string]: string } = {};
  globalFeedbackQuestions: FeedbackQuestionDefinitionDto[] = [];
  submittingFeedback = false;


  // Make Math available in template
  Math = Math;

  constructor(
    private externalDataService: ExternalDataService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    const stateRestored = this.restoreState();

    if (!stateRestored) {
      // Set default date values
      this.filters.dateFrom = this.getDefaultFromDate();
      this.filters.dateTo = this.getDefaultToDate();
    }

    this.loadZones();
    this.loadAgents();
    this.loadGlobalFeedbackQuestions();
    this.loadScrapCollections();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadGlobalFeedbackQuestions(): void {
    this.externalDataService
      .getGlobalSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
          this.globalFeedbackQuestions = (settings.feedbackQuestions || []).filter(q => q.isActive);
        },
        error: (err) => {
          console.error('Error loading feedback questions:', err);
        }
      });
  }
  loadZones(): void {
    this.externalDataService
      .getAllZones()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (zones) => {
          this.zones = zones;
        },
        error: (error) => {
          console.error('Error loading zones:', error);
        },
      });
  }

  loadAgents(): void {
    this.externalDataService
      .getAllAgents(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (agents) => {
          this.agents = agents;
        },
        error: (error) => {
          console.error('Error loading agents:', error);
        },
      });
  }

  loadScrapCollections(): void {
    this.loading = true;
    this.errorMessage = '';

    const pageIndex = this.currentPage - 1;

    // Build filter object
    const filterParams: any = {
      pageIndex: pageIndex,
      pageSize: this.pageSize,
    };

    if (this.filters.dateFrom) {
      filterParams.dateFrom = new Date(this.filters.dateFrom).toISOString();
    }
    if (this.filters.dateTo) {
      // Set time to end of day for dateTo
      const dateTo = new Date(this.filters.dateTo);
      dateTo.setHours(23, 59, 59, 999);
      filterParams.dateTo = dateTo.toISOString();
    }
    if (this.filters.geoZoneId) {
      filterParams.geoZoneId = this.filters.geoZoneId;
    }
    if (this.filters.customerName?.trim()) {
      filterParams.customerName = this.filters.customerName.trim();
    }
    if (this.filters.phoneNumber?.trim()) {
      filterParams.phoneNumber = this.filters.phoneNumber.trim();
    }
    if (this.filters.agentId) {
      filterParams.agentId = this.filters.agentId;
    }
    let currentPaymentMethod = this.filters.paymentMethod;
    if (this.activeTab === 'voucher') {
      currentPaymentMethod = 'voucher';
    }

    if (currentPaymentMethod && currentPaymentMethod !== 'all') {
      filterParams.paymentMethod = currentPaymentMethod;
    }
    if (this.filters.voucherSettlementStatus && this.filters.voucherSettlementStatus !== 'all') {
      filterParams.voucherSettlementStatus = this.filters.voucherSettlementStatus;
    }

    if (this.activeTab === 'feedback') {
      filterParams.requiresFeedback = true;
      if (this.filters.feedbackStatus && this.filters.feedbackStatus !== 'all') {
        filterParams.feedbackStatus = this.filters.feedbackStatus;
      }
    }

    this.externalDataService
      .getScrapCollections(
        filterParams.pageIndex,
        filterParams.pageSize,
        filterParams.dateFrom,
        filterParams.dateTo,
        filterParams.geoZoneId,
        filterParams.customerName,
        filterParams.phoneNumber,
        filterParams.agentId,
        filterParams.paymentMethod,
        filterParams.voucherSettlementStatus,
        filterParams.requiresFeedback,
        filterParams.feedbackStatus,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.scrapCollections = data.items;
          this.totalCount = data.totalCount;
          this.calculateTotalPages();
          this.loading = false;
          this.selectedCollectionIds.clear(); // Clear selection on reload
          this.selectedAgentId = null;
        },
        error: (error) => {
          console.error('Error loading scrap collections:', error);

          if (error.status === 401) {
            this.errorMessage = 'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.errorMessage =
              'Access denied. You do not have permission to view scrap collections.';
          } else if (error.status === 404) {
            this.errorMessage = 'Scrap collections not found.';
          } else if (error.status >= 500) {
            this.errorMessage = 'Server error. Please try again later.';
          } else if (error.status === 0 || error.status === undefined) {
            this.errorMessage =
              'Network error. Please check your connection and try again.';
          } else {
            this.errorMessage =
              'Failed to load scrap collections. Please try again.';
          }

          this.loading = false;
        },
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.saveState();
    this.loadScrapCollections();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 1; // Reset to first page when changing page size
    this.saveState();
    this.loadScrapCollections();
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.saveState();
    this.loadScrapCollections();
  }

  clearFilters(): void {
    this.filters = {
      dateFrom: '',
      dateTo: '',
      geoZoneId: '',
      customerName: '',
      phoneNumber: '',
      agentId: '',
      paymentMethod: 'all',
      voucherSettlementStatus: 'all',
      feedbackStatus: 'all',
    };
    this.currentPage = 1;
    sessionStorage.removeItem(this.STORAGE_KEY);
    this.loadScrapCollections();
  }

  private saveState(): void {
    const state = {
      filters: this.filters,
      currentPage: this.currentPage,
      pageSize: this.pageSize,
      activeTab: this.activeTab,
    };
    sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
  }

  private restoreState(): boolean {
    const savedState = sessionStorage.getItem(this.STORAGE_KEY);
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        this.filters = state.filters || this.filters;
        this.currentPage = state.currentPage || 1;
        this.pageSize = state.pageSize || 10;
        this.activeTab = state.activeTab || 'all';
        return true;
      } catch (e) {
        console.error('Error parsing saved state', e);
        return false;
      }
    }
    return false;
  }

  setActiveTab(tab: 'all' | 'voucher' | 'feedback'): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    this.currentPage = 1;
    this.selectedCollectionIds.clear();
    this.selectedAgentId = null;
    this.saveState();
    this.loadScrapCollections();
  }

  hasActiveFilters(): boolean {
    return !!(
      this.filters.dateFrom ||
      this.filters.dateTo ||
      this.filters.geoZoneId ||
      this.filters.customerName?.trim() ||
      this.filters.phoneNumber?.trim() ||
      this.filters.agentId ||
      (this.filters.paymentMethod && this.filters.paymentMethod !== 'all') ||
      (this.filters.voucherSettlementStatus && this.filters.voucherSettlementStatus !== 'all') ||
      (this.filters.feedbackStatus && this.filters.feedbackStatus !== 'all')
    );
  }

  private getDefaultFromDate(): string {
    const date = new Date();
    date.setDate(date.getDate());
    return date.toISOString().split('T')[0];
  }

  private getDefaultToDate(): string {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date.toISOString().split('T')[0];
  }

  private calculateTotalPages(): void {
    this.totalPages = Math.ceil(this.totalCount / this.pageSize) || 1;
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatCurrency(amount: number): string {
    return `₹${amount.toFixed(2)}`;
  }

  viewCollectionDetails(collection: ScrapCollection): void {
    this.router.navigate(['/scrap-collections-list', collection.id]);
  }

  isCollectionValid(collection: ScrapCollection): boolean {
    // If isValid is not set or is true, collection is valid
    return collection.isValid !== false;
  }

  hasCustomerAnsweredNo(collection: ScrapCollection): boolean {
    const answers = collection.feedbackDetails?.questionAnswers;
    if (!answers || !Array.isArray(answers) || answers.length === 0) {
      return false;
    }
    return answers.some((qa) => {
      if (!qa || qa.answer === undefined || qa.answer === null) return false;
      const ans = String(qa.answer).trim().toLowerCase();
      return ans === 'no' || ans === 'false';
    });
  }

  getNegativeQuestions(collection: ScrapCollection): FeedbackQuestionAnswerDto[] {
    const answers = collection.feedbackDetails?.questionAnswers;
    if (!answers || !Array.isArray(answers)) return [];
    return answers.filter((qa) => {
      if (!qa || qa.answer === undefined || qa.answer === null) return false;
      const ans = String(qa.answer).trim().toLowerCase();
      return ans === 'no' || ans === 'false';
    });
  }

  hasPositiveQuestions(collection: ScrapCollection): boolean {
    const answers = collection.feedbackDetails?.questionAnswers;
    if (!answers || !Array.isArray(answers) || answers.length === 0) return false;
    return answers.some((qa) => {
      if (!qa || qa.answer === undefined || qa.answer === null) return false;
      const ans = String(qa.answer).trim().toLowerCase();
      return ans === 'yes' || ans === 'true';
    });
  }

  getPositiveQuestions(collection: ScrapCollection): FeedbackQuestionAnswerDto[] {
    const answers = collection.feedbackDetails?.questionAnswers;
    if (!answers || !Array.isArray(answers)) return [];
    return answers.filter((qa) => {
      if (!qa || qa.answer === undefined || qa.answer === null) return false;
      const ans = String(qa.answer).trim().toLowerCase();
      return ans === 'yes' || ans === 'true';
    });
  }

  getRowClass(collection: ScrapCollection): { [key: string]: boolean } {
    const isInvalid = !this.isCollectionValid(collection);
    const hasNegativeFeedback =
      this.activeTab === 'feedback' && this.hasCustomerAnsweredNo(collection);

    return {
      'validity-valid': !isInvalid && !hasNegativeFeedback,
      'validity-invalid': isInvalid,
      'row-feedback-negative': hasNegativeFeedback,
    };
  }

  getValidityClass(collection: ScrapCollection): string {
    return this.isCollectionValid(collection)
      ? 'validity-valid'
      : 'validity-invalid';
  }

  getAgentName(agentId: string): string {
    const agent = this.agents.find((a) => a.agentId === agentId);
    return agent ? agent.name : 'N/A';
  }

  getZoneName(zoneId: string): string {
    const zone = this.zones.find((z) => z.id === zoneId);
    return zone ? zone.name : 'N/A';
  }

  getPaymentMethodName(method: number | string | undefined): string {
    if (method === undefined || method === null) return 'N/A';
    const methodStr = String(method);
    switch (methodStr) {
      case '1':
        return 'Cash';
      case '2':
        return 'Bank Transfer';
      case '3':
        return 'UPI';
      case '4':
        return 'Card';
      case '5':
        return 'Voucher';
      default:
        return methodStr || 'N/A';
    }
  }

  isPendingSettlement(collection: ScrapCollection): boolean {
    return (
      (collection.paymentMethod === 5 || collection.paymentMethod === '5') &&
      (collection.voucherSettlementStatus === VoucherSettlementStatus.Pending ||
        collection.voucherSettlementStatus === undefined ||
        collection.voucherSettlementStatus === null)
    );
  }

  // Selection methods
  toggleSelection(collectionId: string): void {
    const collection = this.scrapCollections.find((c) => c.id === collectionId);
    if (!collection) return;

    const agentId = collection.agentDetails?.agentId;

    if (this.selectedCollectionIds.has(collectionId)) {
      this.selectedCollectionIds.delete(collectionId);
      if (this.selectedCollectionIds.size === 0) {
        this.selectedAgentId = null;
      }
    } else {
      if (this.selectedAgentId && this.selectedAgentId !== agentId) {
        this.selectedCollectionIds.clear();
      }
      this.selectedAgentId = agentId || null;
      this.selectedCollectionIds.add(collectionId);
    }
  }

  isSelected(collectionId: string): boolean {
    return this.selectedCollectionIds.has(collectionId);
  }

  selectAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      const firstPending = this.scrapCollections.find((c) => this.isPendingSettlement(c));
      if (firstPending) {
        const agentId = firstPending.agentDetails?.agentId;
        this.selectedAgentId = agentId || null;
        this.scrapCollections.forEach((c) => {
          if (this.isPendingSettlement(c) && c.agentDetails?.agentId === agentId) {
            this.selectedCollectionIds.add(c.id);
          }
        });
      }
    } else {
      this.selectedCollectionIds.clear();
      this.selectedAgentId = null;
    }
  }

  isAllSelected(): boolean {
    const pendingVoucherCollections = this.scrapCollections.filter((c) =>
      this.isPendingSettlement(c),
    );
    if (pendingVoucherCollections.length === 0) return false;

    if (this.selectedAgentId) {
      const agentPendingCollections = pendingVoucherCollections.filter(
        (c) => c.agentDetails?.agentId === this.selectedAgentId
      );
      return (
        agentPendingCollections.length > 0 &&
        agentPendingCollections.every((c) => this.selectedCollectionIds.has(c.id))
      );
    }
    return false;
  }

  hasVoucherCollections(): boolean {
    return this.scrapCollections.some((c) => this.isPendingSettlement(c));
  }

  // Settlement methods
  openSettlementModal(): void {
    if (this.selectedCollectionIds.size === 0) return;
    this.showSettlementModal = true;
    this.settlementStatus = VoucherSettlementStatus.Settled;
    this.settlementNotes = '';
    this.settlementErrorMessage = '';
    
    
    // Set local date string (YYYY-MM-DD)
    const today = new Date();
    const offset = today.getTimezoneOffset();
    const localToday = new Date(today.getTime() - (offset * 60 * 1000));
    this.dateOfCollection = localToday.toISOString().split('T')[0];
  }

  closeSettlementModal(): void {
    this.showSettlementModal = false;
    this.settling = false;
  }

  openSettlementDetailsModal(collection: ScrapCollection): void {
    this.selectedSettlementCollection = collection;
    this.showSettlementDetailsModal = true;
  }

  closeSettlementDetailsModal(): void {
    this.showSettlementDetailsModal = false;
    this.selectedSettlementCollection = null;
  }

  confirmSettlement(): void {
    if (this.selectedCollectionIds.size === 0) return;

    this.settlementErrorMessage = '';

    this.settling = true;
    const request: BulkUpdateVoucherSettlementDto = {
      scrapCollectionIds: Array.from(this.selectedCollectionIds),
      status: this.settlementStatus,
      notes: this.settlementNotes,
      dateOfCollection: this.dateOfCollection ? new Date(this.dateOfCollection).toISOString() : undefined,
    };

    this.externalDataService
      .updateVoucherSettlementBatch(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.closeSettlementModal();
          this.selectedCollectionIds.clear();
          this.selectedAgentId = null;
          this.loadScrapCollections();
        },
        error: (error) => {
          console.error('Error updating voucher settlement:', error);
          this.settlementErrorMessage =
            error?.error?.error || error?.error?.message || 'Failed to update voucher settlement. Please try again.';
          this.errorMessage =
            'Failed to update voucher settlement. Please try again.';
          this.settling = false;
        },
      });
  }

  getTotalSelectedAmount(): number {
    return this.scrapCollections
      .filter((c) => this.selectedCollectionIds.has(c.id))
      .reduce((sum, c) => sum + c.grandTotal, 0);
  }

  getVoucherSettlementStatusName(status?: number): string {
    if (status === undefined || status === null) return 'Pending';
    switch (status) {
      case VoucherSettlementStatus.Pending:
        return 'Pending';
      case VoucherSettlementStatus.Settled:
        return 'Settled';
      case VoucherSettlementStatus.Rejected:
        return 'Rejected';
      default:
        return 'Pending';
    }
  }

  // Feedback Modal Methods
  openFeedbackModal(collection: ScrapCollection): void {
    this.selectedFeedbackCollection = collection;
    this.feedbackHappinessIndex = collection.feedbackDetails?.happinessIndex ?? null;
    this.feedbackCustomerComments = collection.feedbackDetails?.customerComments || '';
    this.feedbackInternalComments = collection.feedbackDetails?.internalComments || '';
    this.feedbackStatus = collection.feedbackDetails?.feedbackStatus || 'Completed';
    this.surveyQuestionAnswers = {};

    // Populate existing answers if any
    if (collection.feedbackDetails?.questionAnswers) {
      collection.feedbackDetails.questionAnswers.forEach(qa => {
        this.surveyQuestionAnswers[qa.questionId] = qa.answer;
      });
    }

    this.showFeedbackModal = true;
  }

  closeFeedbackModal(): void {
    this.showFeedbackModal = false;
    this.selectedFeedbackCollection = null;
    this.submittingFeedback = false;
  }

  saveFeedback(): void {
    if (!this.selectedFeedbackCollection) return;

    this.submittingFeedback = true;
    const questionAnswersList = this.globalFeedbackQuestions.map(q => ({
      questionId: q.id,
      questionText: q.questionText,
      answer: this.surveyQuestionAnswers[q.id] || ''
    }));

    const payload: SaveScrapCollectionFeedbackDto = {
      happinessIndex: this.feedbackHappinessIndex,
      customerComments: this.feedbackCustomerComments,
      internalComments: this.feedbackInternalComments,
      feedbackStatus: this.feedbackStatus,
      questionAnswers: questionAnswersList
    };

    this.externalDataService
      .logScrapCollectionFeedback(this.selectedFeedbackCollection.id, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.closeFeedbackModal();
          this.loadScrapCollections();
        },
        error: (err) => {
          console.error('Error logging feedback:', err);
          this.submittingFeedback = false;
        }
      });
  }

  toggleManualFeedbackTag(collection: ScrapCollection): void {
    const newRequiresFeedback = !collection.requiresFeedback;
    const payload: TagScrapCollectionFeedbackDto = {
      requiresFeedback: newRequiresFeedback,
      reason: newRequiresFeedback ? 'Manually Tagged by Manager' : undefined
    };

    this.externalDataService
      .tagScrapCollectionFeedback(collection.id, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          collection.requiresFeedback = newRequiresFeedback;
          if (newRequiresFeedback) {
            collection.feedbackDetails = collection.feedbackDetails || {
              feedbackReason: 'Manually Tagged by Manager',
              isManuallyTagged: true,
              feedbackStatus: 'Pending'
            };
          } else {
            if (this.activeTab === 'feedback') {
              this.loadScrapCollections();
            }
          }
        },
        error: (err) => {
          console.error('Error toggling manual feedback tag:', err);
        }
      });
  }
}
